/**
 * Adapts the server-injected coupon preview (serverData.couponPreview, hydrated into
 * CouponPreviewStore) into the recurly.js shapes the plan tiles already consume, so promo
 * pricing can render without a client-side recurly.Pricing() round trip.
 *
 * Payload contract (CouponPreviewResponse, per the INTL API swagger):
 *   {
 *     success: true,
 *     items: [{
 *       couponInformation: { code, name, discountType, discountPercent, duration,
 *                            temporalUnit, temporalAmount, freeTrialUnit, freeTrialAmount },
 *       eligiblePlans: [{ planCode, basePrice, discount, subtotal }],   // currency subunits
 *       ineligiblePlans: [{ planCode, reason }],
 *     }],
 *   }
 * discountType is percent | fixed | free_trial, duration is single_use | temporal | forever.
 * The schema does not declare couponInformation nullable, but stage returns null for an unknown
 * coupon (alongside a coupon_not_found ineligiblePlans entry), so every read stays optional.
 * The payload carries no currency information, so callers pass the currency of the plan
 * being rendered ({ symbol, code, subunits }).
 *
 * Every function returns null on missing/malformed data — callers treat null as "no preview,
 * fall back to recurly.js".
 */

// Preview discount types (percent | fixed | free_trial) → recurly discount types.
const DISCOUNT_TYPE_MAP = {
    fixed: 'dollars',
    percent: 'percent',
    free_trial: 'free_trial',
};

const toUnits = (rawAmount, subunits) => (Number.isFinite(rawAmount) ? rawAmount / subunits : NaN);

// Recurly coupon codes are case insensitive.
const findPreviewItem = (items, couponCode) => {
    if (!Array.isArray(items) || !couponCode) {
        return null;
    }

    const wanted = String(couponCode).toLowerCase();

    return (
        items.find(
            (item) => String(item?.couponInformation?.code ?? '').toLowerCase() === wanted,
        ) ?? null
    );
};

const normalizeCurrency = (currency) => {
    const symbol = currency?.symbol;
    const code = currency?.code;
    if (!symbol || !code) {
        return null;
    }
    const subunits = Number(currency?.subunits);
    return { symbol, code, subunits: subunits > 0 ? subunits : 100 };
};

/**
 * Extracts the adapter's currency argument from a Plan/Product model instance.
 *
 * @param {Object} plan  a model with currency / currencySymbol / currency_subunits
 * @returns {{symbol: string, code: string, subunits: number}|null}
 */
export function getPlanCurrency(plan) {
    return normalizeCurrency({
        symbol: plan?.currencySymbol,
        code: plan?.currency,
        subunits: plan?.currency_subunits,
    });
}

/**
 * Builds a recurly-coupon-shaped object from a preview item, matching the fields read by
 * couponsMixin.formatCouponString, coupon.helpers and the plan tiles.
 *
 * @param {Object} couponInformation
 * @param {Array} eligiblePlans
 * @param {{symbol: string, code: string, subunits: number}} currency
 * @returns {Object|null}
 */
export function toRecurlyCoupon(couponInformation, eligiblePlans, currency) {
    const type = DISCOUNT_TYPE_MAP[String(couponInformation?.discountType ?? '').toLowerCase()];
    const code = couponInformation?.code;
    const duration = couponInformation?.duration;
    const normalized = normalizeCurrency(currency);

    if (!type || !code || !duration || !normalized) {
        return null;
    }

    const plans = eligiblePlans.map((plan) => plan?.planCode).filter(Boolean);

    const discount = { type };

    const rate = Number(couponInformation?.discountPercent);
    if (Number.isFinite(rate)) {
        discount.rate = rate / 100;
    }

    // Currency-keyed amount map ({BRL: 5}) the way recurly sends fixed discounts; the
    // per-plan discount of a fixed coupon is the same amount on every plan.
    const discountedPlan = eligiblePlans.find((plan) => Number.isFinite(Number(plan?.discount)));
    if (discountedPlan) {
        discount.amount = {
            [normalized.code]: toUnits(Number(discountedPlan.discount), normalized.subunits),
        };
    }

    const trialUnit = couponInformation?.freeTrialUnit;
    const trialAmount = couponInformation?.freeTrialAmount;
    if (trialUnit && trialAmount) {
        discount.trial = { unit: trialUnit, amount: trialAmount };
    }

    // The formatters would render garbage without the type-specific discount data.
    if (
        (type === 'percent' && !Number.isFinite(rate)) ||
        (type === 'dollars' && !discount.amount) ||
        (type === 'free_trial' && !discount.trial)
    ) {
        return null;
    }

    return {
        code,
        name: couponInformation?.name,
        duration,
        temporal_unit: couponInformation?.temporalUnit ?? null,
        temporal_amount: couponInformation?.temporalAmount ?? null,
        single_use: duration === 'single_use',
        applies_to_all_plans: false,
        plans,
        discount,
    };
}

/**
 * Builds a recurly-Pricing-instance-shaped object from one eligible plan entry.
 *
 * @param {Object} eligiblePlan
 * @param {{symbol: string, code: string, subunits: number}} currency
 * @param {boolean} isFreeTrial  the coupon discounts the first charge only
 * @returns {Object|null}
 */
export function toRecurlyPricing(eligiblePlan, currency, isFreeTrial = false) {
    const normalized = normalizeCurrency(currency);
    if (!normalized) {
        return null;
    }

    const subtotal = toUnits(Number(eligiblePlan?.subtotal), normalized.subunits);
    if (!Number.isFinite(subtotal)) {
        return null;
    }

    const basePrice = toUnits(Number(eligiblePlan?.basePrice), normalized.subunits);

    // recurly.js emits money values as strings ('14.90') and consumers rely on that
    // (e.g. updatePlanDescription calls price.replace()).
    const subtotalString = subtotal.toFixed(2);
    const baseString = (Number.isFinite(basePrice) ? basePrice : subtotal).toFixed(2);

    // The preview reports a single subtotal: the amount of the first charge. A free trial
    // leaves the recurring charge at full price, so `next` keeps the base price — otherwise
    // the annual tile derives its equivalent-monthly copy from a zero subtotal. Every other
    // discount type applies to the recurring charge too, so both sides carry the subtotal.
    const nextString = isFreeTrial ? baseString : subtotalString;

    return {
        currencySymbol: normalized.symbol,
        currencyCode: normalized.code,
        price: {
            now: { subtotal: subtotalString, total: subtotalString },
            next: { subtotal: nextString, total: nextString },
            base: { plan: { unit: baseString } },
            currency: { symbol: normalized.symbol, code: normalized.code },
        },
    };
}

/**
 * The coupon a plan should price with: the campaign per-plan offer (offerCouponCode) wins
 * over the user promo. Resolved directly — not via couponsMixin.getCoupon — because that
 * helper reads the campaign feature flag, which may not be hydrated yet on an early mount;
 * the race made fallbacks price campaign tiles with the raw promo. In promo-only flows the
 * server sets offerCouponCode to the promo, so the precedence is a no-op there.
 *
 * @param {Object} plan    a model with offerCouponCode
 * @param {string|null} promo
 * @returns {string|null|undefined}
 */
export function resolveCouponForPlan(plan, promo) {
    return plan?.offerCouponCode || promo;
}

/**
 * getPreviewPromo for a Plan/Product model: the coupon, plan code and currency all come
 * from the plan being rendered, with the resolveCouponForPlan precedence.
 *
 * @param {Array} items    couponPreview/getCouponPreviewItems
 * @param {Object} plan    a model with recurlyCode / offerCouponCode / currency fields
 * @param {string|null} promo
 * @returns {{coupon: Object, pricing: Object}|null}
 */
export function getPreviewPromoForPlan(items, plan, promo) {
    return getPreviewPromo(
        items,
        resolveCouponForPlan(plan, promo),
        plan?.recurlyCode,
        getPlanCurrency(plan),
    );
}

/**
 * True when the preview carries a verdict for this coupon, and for this plan when a plan code
 * is given. A verdict (eligible or ineligible) is authoritative — re-asking recurly would only
 * produce the same answer as a network error, so callers must not fall back for it. A plan the
 * preview was not built against has no verdict: the plan set can change after render (an
 * ex-sub signing in inside the SPA swaps the trial SKUs for the no-trial ones), and then
 * recurly is the only source that can price the tile.
 *
 * @param {Array} items    couponPreview/getCouponPreviewItems
 * @param {string} couponCode
 * @param {string|null} [planCode]
 * @returns {boolean}
 */
export function previewKnowsCoupon(items, couponCode, planCode = null) {
    const item = findPreviewItem(items, couponCode);

    if (!item) {
        return false;
    }

    if (!planCode) {
        return true;
    }

    const listsPlan = (plans) => (plans ?? []).some((plan) => plan?.planCode === planCode);

    return listsPlan(item.eligiblePlans) || listsPlan(item.ineligiblePlans);
}

/**
 * Looks up a coupon + plan in the preview items and returns the recurly-shaped pair the plan
 * tiles consume, or null when the preview has no usable entry (caller falls back to recurly).
 *
 * @param {Array} items    couponPreview/getCouponPreviewItems
 * @param {string} couponCode
 * @param {string} planCode
 * @param {{symbol: string, code: string, subunits?: number}} currency  from the rendered plan
 * @returns {{coupon: Object, pricing: Object}|null}
 */
export function getPreviewPromo(items, couponCode, planCode, currency) {
    const item = planCode ? findPreviewItem(items, couponCode) : null;

    if (!item) {
        return null;
    }

    const eligiblePlans = item?.eligiblePlans ?? [];
    const eligiblePlan = eligiblePlans.find((plan) => plan?.planCode === planCode);

    if (!eligiblePlan) {
        return null;
    }

    const coupon = toRecurlyCoupon(item?.couponInformation, eligiblePlans, currency);
    const pricing = toRecurlyPricing(
        eligiblePlan,
        currency,
        coupon?.discount?.type === 'free_trial',
    );

    if (!coupon || !pricing) {
        return null;
    }

    return { coupon, pricing };
}
